/**
 * AnimatedGradientBackground Component
 *
 * Three overlapping radial gradients with orbital animation.
 * Remotion-native version inspired by Framer's interactive-animated-gradient.
 *
 * Features:
 * - 3 radial gradients blending together
 * - Orbital animation (gradients rotate around center)
 * - 13 preset color palettes
 * - Configurable orbit radius, speed, blur
 * - GPU-optimized
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, AbsoluteFill, interpolate } from 'remotion';

// Color palette presets
export const GRADIENT_PALETTES = {
  sunset: ['#FF6B6B', '#FFA07A', '#FFD93D'],
  aqua: ['#00D9FF', '#00B4D8', '#0077B6'],
  aurora: ['#00FF87', '#60EFFF', '#7B68EE'],
  candy: ['#FF6FD8', '#FF9A8B', '#FFECD2'],
  forest: ['#134E5E', '#71B280', '#2ECC71'],
  ocean: ['#1A2980', '#26D0CE', '#00CED1'],
  peach: ['#FFAB91', '#FF8A65', '#FF7043'],
  lemon: ['#F9D423', '#FF4E50', '#F7971E'],
  rose: ['#FF6B6B', '#FFC0CB', '#FFB6C1'],
  sky: ['#87CEEB', '#00BFFF', '#1E90FF'],
  lava: ['#FF4500', '#FF6347', '#DC143C'],
  mint: ['#98FF98', '#00FA9A', '#3CB371'],
  grape: ['#8E44AD', '#9B59B6', '#663399'],
  sand: ['#C2B280', '#DEB887', '#D2691E'],
  luxury: ['#D4AF37', '#B8860B', '#FFD700'],
  midnight: ['#191970', '#000080', '#483D8B'],
  neon: ['#FF00FF', '#00FFFF', '#FFFF00'],
} as const;

export type GradientPalette = keyof typeof GRADIENT_PALETTES;

export interface AnimatedGradientBackgroundProps {
  /** Children to render on top */
  children?: React.ReactNode;
  /** Color palette preset */
  palette?: GradientPalette;
  /** Custom colors (overrides palette) - array of 3 hex colors */
  customColors?: [string, string, string];
  /** Duration of one full orbit in seconds */
  loopDuration?: number;
  /** Orbit radius as percentage (0-50) */
  orbitRadius?: number;
  /** Blur amount in pixels */
  blur?: number;
  /** Brightness multiplier (0.2-1.4) */
  brightness?: number;
  /** Background color behind gradients */
  backgroundColor?: string;
  /** Animation speed multiplier */
  speed?: number;
  /** Start the animation from a specific phase (0-1) */
  initialPhase?: number;
}

export const AnimatedGradientBackground: React.FC<AnimatedGradientBackgroundProps> = ({
  children,
  palette = 'aurora',
  customColors,
  loopDuration = 8,
  orbitRadius = 25,
  blur = 80,
  brightness = 1,
  backgroundColor = '#0a0a0a',
  speed = 1,
  initialPhase = 0,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Get colors from palette or custom
  const colors = customColors || GRADIENT_PALETTES[palette];
  const [color1, color2, color3] = colors;

  // Calculate phase based on frame (0 to 2π)
  const cycleDuration = loopDuration * fps / speed;
  const phase = ((frame / cycleDuration) + initialPhase) * Math.PI * 2;

  // Center point
  const centerX = 50;
  const centerY = 50;

  // Calculate positions for each gradient lobe
  // Primary lobe at phase, secondary and tertiary at 120° intervals
  const x1 = centerX + Math.cos(phase) * orbitRadius;
  const y1 = centerY + Math.sin(phase) * orbitRadius;

  const x2 = centerX + Math.cos(phase + (Math.PI * 2 / 3)) * orbitRadius;
  const y2 = centerY + Math.sin(phase + (Math.PI * 2 / 3)) * orbitRadius;

  const x3 = centerX + Math.cos(phase + (Math.PI * 4 / 3)) * orbitRadius;
  const y3 = centerY + Math.sin(phase + (Math.PI * 4 / 3)) * orbitRadius;

  // Build the gradient background
  const gradientBackground = `
    radial-gradient(circle at ${x1}% ${y1}%, ${color1} 0%, ${color1} 22%, rgba(0,0,0,0) 60%),
    radial-gradient(circle at ${x2}% ${y2}%, ${color2} 0%, ${color2} 22%, rgba(0,0,0,0) 60%),
    radial-gradient(circle at ${x3}% ${y3}%, ${color3} 0%, ${color3} 22%, rgba(0,0,0,0) 60%)
  `;

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        overflow: 'hidden',
      }}
    >
      {/* Animated gradient layer */}
      <AbsoluteFill
        style={{
          background: gradientBackground,
          filter: `blur(${blur}px) brightness(${brightness})`,
          willChange: 'background',
        }}
      />

      {/* Optional noise overlay for texture */}
      <AbsoluteFill
        style={{
          opacity: 0.03,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px',
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
        }}
      />

      {/* Children content */}
      <AbsoluteFill
        style={{
          zIndex: 10,
        }}
      >
        {children}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

export default AnimatedGradientBackground;
